import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const UserEdit = () => {
    const { id } = useParams();
    const history = useHistory();
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [formData, setFormData] = useState({
        FirstName: '',
        LastName: '',
        UserName: '',
        Email: '',
        ContactNumber: '',
    });

    useEffect(() => {
        fetchData('UserApi', `/UserDetails/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching User Details:', error);
            });
    }, [id]);

    const handleChange = (event) => {
        const { name, value, type } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? !prevData[name] : value,
        }));

        if (name === 'FirstName' || name === 'LastName') {
            const firstName = name === 'FirstName' ? value : formData.FirstName;
            const lastName = name === 'LastName' ? value : formData.LastName;
            const updatedUserName = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
            const limitedUserName = updatedUserName.slice(0, 15);
            setFormData((prevFormData) => ({
                ...prevFormData,
                [name]: value,
                UserName: limitedUserName,
            }));
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsLoading(true);

        if (!formData.FirstName || !formData.LastName) {
            setErrorMessage('First Name and Last Name are required!');
            setIsLoading(false);
            return;
        }

        const updatedData = {
            ...formData,
            UpdatedBy: userId,
        };

        updateData('UserApi', `${id}`, updatedData, token)
            .then(() => {
                setSuccessMessage('User updated successfully!');
                setErrorMessage('');
                history.push('/UserIndex');
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setErrorMessage('Failed to update user.');
                setIsLoading(false);
            });
    };

    return (
        <div className="form-container">
            <div className="form-card">
                {/* Header */}
                <div className="form-header">
                    <h1 className="form-title">Edit User</h1>
                    <div className="form-actions">
                        <Link to="/UserIndex" className="btn-back">
                            <ArrowBackIcon fontSize="small" />
                            Back to List
                        </Link>
                        <button className="btn-save" onClick={handleSubmit} disabled={isLoading}>
                            {isLoading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <>
                                    <SaveIcon fontSize="small" />
                                    Update
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Messages */}
                {errorMessage && <div className="message error">{errorMessage}</div>}
                {successMessage && <div className="message success">{successMessage}</div>}

                {/* Form Body */}
                <div className="form-body">
                    {/* Row 1 - Names */}
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">First Name *</label>
                            <input
                                className="form-input"
                                value={formData.FirstName || ''}
                                name="FirstName"
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter First Name"
                                maxLength={30}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Last Name *</label>
                            <input
                                className="form-input"
                                value={formData.LastName || ''}
                                name="LastName"
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter Last Name"
                                maxLength={30}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">User Name</label>
                            <input
                                className="form-input"
                                value={formData.UserName || ''}
                                name="UserName"
                                type="text"
                                placeholder="Auto-generated"
                                readOnly
                                style={{ backgroundColor: '#f8fafc' }}
                            />
                        </div>
                    </div>

                    {/* Row 2 - Contact */}
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Contact Number *</label>
                            <input
                                className="form-input"
                                value={formData.ContactNumber || ''}
                                name="ContactNumber"
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter Contact Number"
                                maxLength={15}
                            />
                        </div>
                        <div className="form-group" style={{ flex: 2 }}>
                            <label className="form-label">Email *</label>
                            <input
                                className="form-input"
                                value={formData.Email || ''}
                                name="Email"
                                onChange={handleChange}
                                type="email"
                                placeholder="Enter Email"
                                maxLength={40}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserEdit;
